/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Trophy, Users, HeartHandshake, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { ASSETS } from "@/constants/site";

const HIGHLIGHTS = [
    { icon: Trophy, title: "Proven Community Leader", text: "Two decades of service, board roles, and recognized impact across East Tennessee." },
    { icon: Sparkles, title: "Award-Winning Entrepreneur", text: "Honored for entrepreneurial leadership and community contribution." },
    { icon: Users, title: "Experienced Counselor", text: "LADAC II, NCAC I, QCS, SAP — clinically credentialed at the highest professional level." },
    { icon: HeartHandshake, title: "Skilled Grant Writer & Fundraiser", text: "Funding secured for housing, recovery, workforce, and community programs." },
];

export default function Impact() {
    const [stats, setStats] = useState([]);

    useEffect(() => {
        api.get("/impact").then(({ data }) => setStats(data));
    }, []);

    return (
        <div data-testid="impact-page">
            <section className="bg-[#0A192F] text-white py-28">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <span className="overline">Impact</span>
                    <h1 className="font-serif text-5xl sm:text-6xl font-bold mt-4 leading-tight max-w-4xl">
                        Programs, partnerships,{" "}
                        <span className="italic text-[#D4AF37]">possibilities.</span>
                    </h1>
                    <p className="mt-8 text-lg text-slate-300 max-w-2xl leading-relaxed">
                        The work isn't theoretical. These are the numbers,
                        recognitions, and outcomes Reico and his teams have
                        built across Knoxville and East Tennessee.
                    </p>
                </div>
            </section>

            <section className="py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 sm:divide-x sm:divide-y-0 divide-y divide-slate-200 border border-slate-200 bg-white">
                        {stats.map((s) => (
                            <div
                                key={s.id}
                                data-testid={`impact-stat-${s.id}`}
                                className="p-8 lg:p-10 text-center"
                            >
                                <div className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#D4AF37] leading-none break-words">
                                    {s.value}
                                </div>
                                <div className="mt-4 text-[11px] uppercase tracking-[0.2em] font-semibold text-[#0A192F]">
                                    {s.label}
                                </div>
                                {s.description && (
                                    <div className="mt-2 text-xs text-slate-500">
                                        {s.description}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-24">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="mb-14 max-w-3xl">
                        <span className="overline">Why Work With Reico</span>
                        <h2 className="font-serif text-4xl sm:text-5xl font-bold mt-4 text-[#0A192F] leading-tight">
                            Credentialed. Connected. Committed.
                        </h2>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {HIGHLIGHTS.map((h) => (
                            <div key={h.title} className="exec-card p-7">
                                <div className="w-12 h-12 bg-[#0A192F] flex items-center justify-center rounded-sm">
                                    <h.icon className="w-5 h-5 text-[#D4AF37]" />
                                </div>
                                <h3 className="font-serif text-xl font-bold mt-5 text-[#0A192F]">
                                    {h.title}
                                </h3>
                                <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                                    {h.text}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-slate-50 py-24">
                <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-5">
                        <div className="w-full aspect-[4/3] bg-slate-100 overflow-hidden rounded-sm shadow-md border border-slate-200 flex items-center justify-center">
                            <img
                                src={ASSETS.consultingMeeting}
                                alt="Reico Hopewell at The HopeWell Foundation Panel Speakers event"
                                loading="lazy"
                                className="w-full h-full object-contain"
                            />
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0A192F] leading-tight">
                            "True success is measured not by what we accumulate
                            for ourselves, but by what we create for others."
                        </h2>
                        <p className="mt-6 text-sm uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
                            — Reico S. Hopewell
                        </p>
                        <p className="mt-6 text-slate-700 leading-relaxed">
                            Every life transformed, every business launched,
                            every person in recovery, every scholarship
                            awarded, and every opportunity created
                            strengthens our community for future generations.
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-20 bg-[#0A192F] text-white">
                <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
                    <h3 className="font-serif text-3xl sm:text-4xl font-bold leading-tight">
                        Let's add your project to this story.
                    </h3>
                    <Link
                        to="/contact"
                        data-testid="impact-cta"
                        className="mt-8 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A192F] hover:bg-[#C5A059] rounded-sm px-8 py-4 font-bold text-sm uppercase tracking-widest"
                    >
                        Schedule a Consultation <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
